import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import { Box, Button, Card, CardContent, Container, Fab, Grid, Stack, Typography } from '@mui/material';
import PageBanner from '../../components/Common/PageBanner';
import ProductImageSlider from '../../components/Products/ProductImageSlider';
import { pageBannerHomeLink, productRanges } from '../../data/siteData';
import { formatProductName, getProductImages } from '../../utils/productAssets';

function CategoryDetailPage() {
  const { category } = useParams();
  const navigate = useNavigate();

  const selectedRange = useMemo(
    () => productRanges.find((range) => range.slug === category),
    [category]
  );

  const products = useMemo(() => {
    if (!selectedRange) {
      return [];
    }

    const productDescriptions = new Map(
      selectedRange.products.map((product) => [product.filename.toLowerCase(), product])
    );

    return getProductImages(selectedRange.assetFolderName).map((image) => {
      const product = productDescriptions.get(image.filename.toLowerCase());
      const name = product?.name || formatProductName(image.filename);

      return {
        ...image,
        name,
        alt: name,
        description: product?.description || selectedRange.description,
      };
    });
  }, [selectedRange]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!selectedRange) {
    return (
      <Box>
        <PageBanner
          title="Product Range Not Found"
          subtitle="The selected product category could not be found."
          breadcrumb={[
            pageBannerHomeLink,
            { label: 'Products', path: '/products' },
            { label: 'Not Found', path: `/products/${category}` },
          ]}
        />
        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
          <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate('/products')}>
            Back to Products
          </Button>
        </Container>
      </Box>
    );
  }

  return (
    <Box>
      <PageBanner
        title={selectedRange.label || selectedRange.name}
        subtitle={selectedRange.description}
        breadcrumb={[
          pageBannerHomeLink,
          { label: 'Products', path: '/products' },
          { label: selectedRange.label || selectedRange.name, path: `/products/${selectedRange.slug}` },
        ]}
      />

      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
          <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate('/products')}>
            Back to Products
          </Button>
          <Typography sx={{ color: 'text.secondary', fontWeight: 800 }}>
            {products.length} Products
          </Typography>
        </Stack>

        <Grid container spacing={3}>
          {products.map((product) => (
            <Grid item xs={12} sm={6} md={4} key={product.filename}>
              <Card elevation={0} sx={{ height: '100%' }}>
                <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <ProductImageSlider images={[product]} height={260} />
                  <Typography variant="h4" sx={{ mt: 2.5, mb: 1 }}>
                    {product.name}
                  </Typography>
                  <Typography sx={{ color: 'text.secondary', lineHeight: 1.75 }}>
                    {product.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {!products.length && (
          <Box
            sx={{
              py: 8,
              mt: 2,
              textAlign: 'center',
              borderRadius: 3,
              bgcolor: '#fffdfa',
              border: '1px solid rgba(74,14,143,0.1)',
            }}
          >
            <Typography variant="h4" sx={{ mb: 1 }}>
              No products found
            </Typography>
            <Typography sx={{ color: 'text.secondary' }}>
              Add images to this category asset folder to display them here.
            </Typography>
          </Box>
        )}
      </Container>

      <Fab
        variant="extended"
        color="secondary"
        onClick={scrollToTop}
        sx={{ position: 'fixed', right: 20, bottom: 156, zIndex: 1400 }}
      >
        <KeyboardArrowUpRoundedIcon sx={{ mr: 0.75 }} />
        Back to Top
      </Fab>
    </Box>
  );
}

export default CategoryDetailPage;
